/**
 * Upload and ingest status.
 *
 * The rule this view exists to honour: **failures are individual and explicit**. One
 * corrupt file in a batch of two hundred fails alone, says why, and leaves the other
 * hundred and ninety-nine searchable. A single aggregate "3 files failed" would make
 * an investigator wonder which three, and quietly erode trust in the whole corpus.
 */

import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { api, type Dataset, type FileStatus } from "../lib/api";

const POLL_MS = 2000;

export function Files({ datasetId }: { datasetId: string }) {
  const [files, setFiles] = useState<FileStatus[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState<string[]>([]);
  const input = useRef<HTMLInputElement>(null);
  const [dataset, setDataset] = useState<Dataset | null>(null);
  const [language, setLanguage] = useState("");
  const [savingLanguage, setSavingLanguage] = useState(false);
  const [languageError, setLanguageError] = useState<string | null>(null);

  useEffect(() => {
    void api.dataset(datasetId).then((d) => {
      setDataset(d);
      setLanguage(d.language ?? "");
    });
  }, [datasetId]);

  async function saveLanguage(event: FormEvent) {
    event.preventDefault();
    if (!dataset) return;
    setSavingLanguage(true);
    setLanguageError(null);
    try {
      const updated = await api.updateDataset(datasetId, {
        name: dataset.name,
        description: dataset.description,
        language: language.trim() || null,
      });
      setDataset(updated);
    } catch (cause) {
      setLanguageError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setSavingLanguage(false);
    }
  }

  const refresh = useCallback(async () => {
    try {
      setFiles(await api.files(datasetId));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }, [datasetId]);

  useEffect(() => {
    void refresh();
    // Polling, not SSE: ingest is minutes-long background work, and a dropped socket
    // during a long import would be a worse failure than a two-second delay.
    const timer = window.setInterval(() => void refresh(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [refresh]);

  async function upload(chosen: FileList | null) {
    if (!chosen) return;
    const names = Array.from(chosen).map((f) => f.name);
    setUploading(names);
    setError(null);
    for (const file of Array.from(chosen)) {
      try {
        const result = await api.upload(datasetId, file);
        if (result.already_present) {
          // Content addressing means re-uploading the same bytes is a no-op, not a
          // duplicate. Saying so is friendlier than a silent nothing.
          setError(`${file.name} was already in this dataset — same sha256, nothing to do.`);
        }
      } catch (cause) {
        setError(`${file.name}: ${cause instanceof Error ? cause.message : String(cause)}`);
      }
    }
    setUploading([]);
    if (input.current) input.current.value = "";
    await refresh();
  }

  return (
    <main className="page">
      <h1>Files</h1>

      <form onSubmit={saveLanguage} className="card" data-testid="dataset-settings">
        <label htmlFor="dataset-language">Language</label>
        <p className="mono" style={{ color: "var(--ink-faint)", margin: 0 }}>
          A BCP-47 code (e.g. <code>uk</code>) turns on folded substring search for
          Ukrainian case endings. Leave blank for none.
        </p>
        <div className="row">
          <input
            id="dataset-language"
            aria-label="Dataset language"
            placeholder="uk"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            disabled={!dataset}
          />
          <button className="primary" type="submit" disabled={!dataset || savingLanguage}>
            {savingLanguage ? "…" : "Save"}
          </button>
        </div>
        {languageError && (
          <p data-testid="dataset-settings-error" style={{ color: "var(--danger)" }}>
            {languageError}
          </p>
        )}
      </form>

      <div className="card upload">
        <input
          ref={input}
          type="file"
          multiple
          aria-label="Upload files"
          data-testid="file-input"
          onChange={(e) => void upload(e.target.files)}
        />
        <p className="mono" style={{ color: "var(--ink-faint)", margin: 0 }}>
          txt · pdf · html · json · docx · csv · mbox · Telegram export
        </p>
      </div>

      {uploading.length > 0 && <p data-testid="uploading">uploading {uploading.join(", ")}…</p>}
      {error && (
        <p data-testid="files-error" style={{ color: "var(--danger)" }}>
          {error}
        </p>
      )}

      {/* Six columns, one of them a filename and one a hash: wider than a phone at
          any font size. The overflow belongs to the table, not to the page — a page
          that scrolls sideways takes the heading and the upload control with it. */}
      <div className="table-scroll">
        <table className="grid" data-testid="file-table">
          <thead>
            <tr>
              <th>File</th>
              <th>Status</th>
              <th>Documents</th>
              <th>Blocks</th>
              <th>sha256</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {files?.map((file) => (
              <tr key={file.file_id} data-testid="file-row" data-status={file.status}>
                <td>{file.original_name}</td>
                <td>
                  <span className={`status ${file.status}`} data-testid="file-status">
                    {file.status}
                  </span>
                  {file.error && (
                    <div data-testid="file-error" style={{ color: "var(--danger)" }}>
                      {file.error}
                    </div>
                  )}
                </td>
                <td className="mono">{file.documents}</td>
                <td className="mono">{file.blocks}</td>
                <td className="mono" title={file.sha256}>
                  {file.sha256.slice(0, 12)}…
                </td>
                <td>
                  {file.status === "ready" && (
                    // Shown for every ready file, not only tabular ones: the client has
                    // no field to tell a spreadsheet apart from a PDF ahead of asking,
                    // and the review screen itself answers cleanly (a 404 with a plain
                    // reason) when a file turns out not to be tabular.
                    <a
                      href={`#/d/${datasetId}/files/table-profile/${file.file_id}`}
                      data-testid="review-headers-link"
                    >
                      Review headers
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {files?.length === 0 && (
        <p data-testid="files-empty" style={{ color: "var(--ink-faint)" }}>
          No files yet.
        </p>
      )}
    </main>
  );
}
